import { useEffect, useRef, useState } from 'react';
import { Send, Loader2, Info } from 'lucide-react';
import { useRealtimeChat, ChatUser } from '../../hooks/useRealtimeChat';
import { ChatMessageItem } from './ChatMessageItem';

import { Chat } from '../../types';

interface ChatWindowProps {
    chatId: string;
    currentUser: ChatUser;
    otherParticipantName: string;
    onClose?: () => void; // Optional for mobile/widget back button
    activeChat?: Chat;
}

export function ChatWindow({ chatId, currentUser, otherParticipantName, onClose, activeChat }: ChatWindowProps) {
    const [newMessage, setNewMessage] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const {
        messages,
        sendMessage: sendRealtimeMessage,
        isConnected,
        isLoading
    } = useRealtimeChat({
        roomName: chatId,
        currentUser,
        enabled: !!chatId
    });

    // Auto-scroll on new messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newMessage.trim()) return;

        await sendRealtimeMessage(newMessage);
        setNewMessage('');
    };

    return (
        <div className="flex flex-col h-full bg-white dark:bg-gray-900">
            {/* Header - Minimal if embedded, can be enhanced */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0 bg-white dark:bg-gray-800">
                <div className="flex items-center gap-2">
                    {onClose && (
                        <button onClick={onClose} className="text-sm text-gray-500 md:hidden">
                            &larr;
                        </button>
                    )}
                    <div>
                        <span className="font-bold text-gray-900 dark:text-gray-100 block">{otherParticipantName}</span>
                    </div>
                </div>
                <span className={`w-2.5 h-2.5 rounded-full ${isConnected ? 'bg-green-500' : 'bg-gray-400'}`} title={isConnected ? 'Connected' : 'Disconnected'} />
            </div>

            {/* Inquiry Context Card */}
            {(activeChat as any)?.inquiry && (
                <div className="bg-rose-50 dark:bg-rose-900/10 border-b border-rose-100 dark:border-rose-900/20 p-4">
                    <div className="flex justify-between items-start">
                        <div>
                            <span className="text-xs font-bold text-rose-600 dark:text-rose-400 uppercase tracking-wider">Inquiry Details</span>
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-200 mt-1">
                                {(activeChat as any).inquiry.message || 'No message'}
                            </p>
                            {(activeChat as any).inquiry.event_date && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
                                    📅 Event: {(activeChat as any).inquiry.event_date}
                                </p>
                            )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            <span className="px-2 py-1 bg-white dark:bg-gray-800 text-xs rounded border border-gray-200 dark:border-gray-700 shadow-sm">
                                {(activeChat as any).inquiry.status}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto min-h-0 p-4 bg-gray-50 dark:bg-gray-900 scroll-smooth">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <Loader2 className="h-8 w-8 animate-spin mb-2" />
                        <p>Loading messages...</p>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <Info className="h-12 w-12 mb-4 opacity-20" />
                        <p>No messages yet.</p>
                        <p className="text-sm">Start the conversation with {otherParticipantName}!</p>
                    </div>
                ) : (
                    <>
                        {messages.map((msg, idx) => {
                            const isOwnMessage = msg.user.id === currentUser.id;
                            const showHeader = idx === 0 || messages[idx - 1].user.id !== msg.user.id;

                            // Date separator check (simple version)
                            const showDateSeparator = idx === 0 ||
                                new Date(msg.createdAt).toDateString() !== new Date(messages[idx - 1].createdAt).toDateString();

                            return (
                                <div key={msg.id}>
                                    {showDateSeparator && (
                                        <div className="flex justify-center my-4">
                                            <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-3 py-1 rounded-full">
                                                {new Date(msg.createdAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                    )}
                                    <ChatMessageItem
                                        message={msg}
                                        isOwnMessage={isOwnMessage}
                                        showHeader={showHeader}
                                    />
                                </div>
                            );
                        })}
                        <div ref={messagesEndRef} />
                    </>
                )}
            </div>

            {/* Input Area */}
            <form onSubmit={handleSend} className="p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex gap-2">
                <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                />
                <button
                    type="submit"
                    disabled={!newMessage.trim() || isLoading}
                    className="p-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                    <Send className="h-5 w-5" />
                </button>
            </form>
        </div>
    );
}
